import SeriesCommentQ from '@/queries/SeriesCommentQ'
import SeriesHostQ from '@/queries/SeriesHostQ'
import SeriesQ from '@/queries/SeriesQ'
import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import type { Query } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'
import { getBodyForHiddenEntity } from '@/client/services/hidden'

import Discussion from '@/components/Discussion/Discussion'
import type { DiscussionCommentProps } from '@/components/Discussion/types'
import { useInfiniteScroll } from '@/components/Session/helpers'

export default function SeriesDiscussion({ series }: { series: Query }): React.ReactElement {
  const [limit, handleLoadMore] = useInfiniteScroll({
    queryKey: 'limit',
    defaultValue: 10,
  })

  const currentUser = useUser()

  const tutorIds = series.many(SeriesQ.getHosts({})).map(tutor => tutor(SeriesHostQ.user_id) ?? 0)

  const seriesId = series(SeriesQ.id) ?? 0

  const [handlePost] = useAPIWrapper(
    async (message: string) => {
      await RPCRefresh.postSeriesComment({ seriesId, message })
    },
    [seriesId],
    'Post Series Comment'
  )

  const [handleDelete] = useAPIWrapper(
    async (commentId: number) => {
      await RPCRefresh.deleteSeriesComment({ commentId })
    },
    [],
    'Delete Series Comment'
  )

  const discussionComments = series.many(
    SeriesCommentQ.where({
      seriesId: SeriesQ.id,
      order: [[SeriesCommentQ.created_at, 'desc']],
      include: [{ association: 'User', required: true }],
      limit: limit + 1,
    })
  )
  const hasMore = discussionComments.length > limit
  const relevantDiscussionComments: DiscussionCommentProps[] = discussionComments
    .slice(0, limit)
    .map(seriesComment => {
      const id = seriesComment(SeriesCommentQ.id) ?? 0
      const createdAt = `${seriesComment(SeriesCommentQ.created_at)}`
      const hiddenAt = seriesComment(SeriesCommentQ.hidden_at)

      const userId = seriesComment(UserQ.id) ?? 0
      const avatarUrl = seriesComment(UserQ.avatar_url) ?? ''
      const progressionLevel = seriesComment(UserQ.progression_level)
      const displayName = seriesComment(UserQ.display_name) ?? ''
      const username = seriesComment(UserQ.username)

      let message = seriesComment(SeriesCommentQ.message_safe) ?? ''
      if (hiddenAt != null) {
        message = getBodyForHiddenEntity('series-comment', 'plain-text')
      }

      return {
        id,
        message,
        createdAt,
        isHidden: hiddenAt != null,
        user: {
          id: userId,
          avatarUrl,
          username,
          progressionLevel,
          displayName,
          isTutor: tutorIds.includes(userId),
        },
      }
    })

  return (
    <Discussion
      type="series"
      isTutor={tutorIds.includes(currentUser.id)}
      comments={relevantDiscussionComments}
      onPost={handlePost}
      onDelete={handleDelete}
      hasMore={hasMore}
      onLoadMore={handleLoadMore}
    />
  )
}
